import { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import type { BrandWizardStep } from '@mg/shared';
import { cn } from '../../lib/cn';
import { stepPill, WIZARD_STEPS } from './steps';

/**
 * The step list (spec:95; web.md §4.1): eight numbered pills on one line that never wraps, an ordered
 * list with `aria-current="step"` on the current one. Done steps show a check (and ", done" to a screen
 * reader), the current one is filled, upcoming ones are outlined. Every pill can be clicked; leaving the
 * step goes through the wizard's unsaved-changes rule. Narrower than the eight, the row scrolls sideways
 * and keeps the current pill in view by its own scroll position, never by scrolling the page.
 */
export function StepPills({
  step,
  done,
  disabled,
  onSelect,
}: {
  step: BrandWizardStep;
  done: readonly BrandWizardStep[];
  disabled: boolean;
  onSelect: (step: BrandWizardStep) => void;
}) {
  const list = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const row = list.current;
    const pill = row?.querySelector<HTMLElement>(`[data-step="${step}"]`);
    if (!row || !pill) return;
    // The row is `relative`, so a pill's offsetLeft is measured from the row's own left edge.
    const left = pill.offsetLeft;
    const right = left + pill.offsetWidth;
    if (left < row.scrollLeft) row.scrollLeft = left;
    else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth;
  }, [step]);

  return (
    <nav aria-label="Brand Setup steps">
      <ol ref={list} className="relative flex flex-nowrap gap-1.5 overflow-x-auto pb-1">
        {WIZARD_STEPS.map((n) => {
          const current = n === step;
          const isDone = done.includes(n);
          return (
            <li key={n} data-step={n} className="shrink-0">
              <button
                type="button"
                aria-current={current ? 'step' : undefined}
                disabled={disabled}
                onClick={() => {
                  if (!current) onSelect(n);
                }}
                className={cn(
                  'focus-ring inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-sm font-medium disabled:cursor-not-allowed',
                  current
                    ? 'bg-action text-action-fg'
                    : isDone
                      ? 'bg-teal-tint text-teal-fg'
                      : 'border border-line-input bg-card text-ink-label hover:bg-hover',
                )}
              >
                {isDone ? (
                  <Check className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                  <span aria-hidden className="tabular-nums">
                    {n}
                  </span>
                )}
                <span>
                  <span className="sr-only">Step {n}: </span>
                  {stepPill(n)}
                  {isDone && <span className="sr-only">, done</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
